import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut as fbSignOut } from 'firebase/auth';
import { auth, requestNotificationPermissionAndSaveToken } from '../shared/firebase';
import { fetchUserProfile, fetchDriver } from '../shared/services/userService';

const AuthContext = createContext(null);

const DRIVER_ROLES = new Set(['driver', 'delivery', 'deliveryDriver', 'admin']);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  const loadProfile = useCallback(async (fbUser) => {
    const profile = await fetchUserProfile(fbUser.uid);
    setUserProfile(profile);
    const driverDoc = await fetchDriver(fbUser.uid);
    const roleOk = profile && (DRIVER_ROLES.has(profile.role) || profile.role === 'driver' || driverDoc);
    if (!roleOk) {
      setRoleError('This account is not registered as a driver. Please sign in with a driver account.');
      setDriver(null);
      return;
    }
    setRoleError(null);
    setDriver(driverDoc || { id: fbUser.uid, userId: fbUser.uid, name: profile.name || '', isOnline: false });
    requestNotificationPermissionAndSaveToken(fbUser).catch(() => {});
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser) => {
      setUser(fbUser);
      if (fbUser) {
        try { await loadProfile(fbUser); }
        catch (e) { console.error('profile load', e); setRoleError(e.message); }
      } else {
        setUserProfile(null);
        setDriver(null);
        setRoleError(null);
      }
      setLoading(false);
    });
  }, [loadProfile]);

  const signIn = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const signOut = () => fbSignOut(auth);
  const refreshDriver = async () => {
    if (user) setDriver(await fetchDriver(user.uid));
  };

  return (
    <AuthContext.Provider value={{
      user, userProfile, driver, loading, roleError,
      signIn, signOut, refreshDriver,
      driverId: driver?.id || user?.uid || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
